'use client'

import { Sparkles, X, AlertTriangle, AlertCircle, CheckCircle } from 'lucide-react'
import Link from 'next/link'
import type { Announcement, AnnouncementType } from '@/lib/config/announcements'

interface WhatsNewModalProps {
  announcements: Announcement[]
  onDismiss: (ids: string[]) => void
}

const TYPE_BADGE: Record<AnnouncementType, { label: string; color: string; icon: typeof Sparkles }> = {
  feature:  { label: 'New', color: 'bg-[var(--color-brand-indigo)]/10 text-[var(--color-brand-indigo)]', icon: Sparkles },
  warning:  { label: 'Warning', color: 'bg-[var(--color-status-warning)]/10 text-[var(--color-status-warning)]', icon: AlertTriangle },
  problem:  { label: 'Issue', color: 'bg-[var(--color-status-error)]/10 text-[var(--color-status-error)]', icon: AlertCircle },
  resolved: { label: 'Resolved', color: 'bg-[var(--color-status-success)]/10 text-[var(--color-status-success)]', icon: CheckCircle },
}

export function WhatsNewModal({ announcements, onDismiss }: WhatsNewModalProps) {
  if (announcements.length === 0) return null

  const ids = announcements.map((a) => a.id)

  const handleDismiss = () => {
    onDismiss(ids)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div
        className="relative w-full max-w-md rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-6 shadow-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="whats-new-heading"
        data-testid="whats-new-modal"
      >
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <h2
          id="whats-new-heading"
          className="mb-4 text-lg font-bold text-[var(--color-text-primary)]"
          style={{ fontFamily: 'var(--font-heading, "Plus Jakarta Sans", sans-serif)' }}
        >
          What&apos;s New
        </h2>

        <div className="space-y-4">
          {announcements.map((a) => {
            const badge = TYPE_BADGE[a.type] ?? TYPE_BADGE.feature
            const BadgeIcon = badge.icon
            return (
              <div key={a.id} className="rounded-lg border border-[var(--color-border-default)] p-3">
                <div className="mb-1.5 flex items-center gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.color}`}>
                    <BadgeIcon className="h-3 w-3" />
                    {badge.label}
                  </span>
                </div>
                <p className="text-sm font-semibold text-[var(--color-text-primary)]">{a.title}</p>
                <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">{a.body}</p>
                {a.href && (
                  <Link
                    href={a.href}
                    // Announcements often point at auth-gated routes (/feed);
                    // nofollow so this sitewide widget doesn't link to a
                    // redirect (Ahrefs "links to redirect" 2026-09-14).
                    rel="nofollow"
                    className="mt-1.5 inline-block text-xs font-medium text-[var(--color-brand-indigo)] hover:underline"
                  >
                    Learn more
                  </Link>
                )}
              </div>
            )
          })}
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          className="mt-5 w-full rounded-lg bg-[var(--color-brand-indigo)] px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          Got it
        </button>
      </div>
    </div>
  )
}
